import { useCallback, useRef } from 'react'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { getCurrentDirection } from '@/i18n/config'
import { HELP_PANEL_DEFAULT_SIZE } from '@/store/helpAgentSlice'
import {
  HELP_PANEL_END_MARGIN_PX,
  HELP_PANEL_INSET,
  HELP_PANEL_START_OFFSET_PX,
  HELP_PANEL_TOP_CLEARANCE_PX,
  HELP_WIDGET_Z,
} from './constants'

/**
 * Keeps the whole card, and so its Close button, on screen however narrow the
 * viewport: the slice clamp only runs on a resize and never goes below its
 * 300px floor, which a phone beside the rail cannot fit.
 */
export const HELP_PANEL_MAX_WIDTH = `calc(100vw - ${HELP_PANEL_START_OFFSET_PX + HELP_PANEL_END_MARGIN_PX}px)`
/** Same idea upward: the card rises from the foot and must stop short of the header. */
export const HELP_PANEL_MAX_HEIGHT = `calc(100vh - ${HELP_PANEL_TOP_CLEARANCE_PX + HELP_PANEL_END_MARGIN_PX}px)`
import HelpAgentHeader from './HelpAgentHeader'
import HelpAgentInput from './HelpAgentInput'
import HelpAgentMessages from './HelpAgentMessages'
import RatingView from './RatingView'
import SuggestedQuestions from './SuggestedQuestions'
import ThanksView from './ThanksView'

type ResizeAxis = 'x' | 'y' | 'both'

/**
 * Chat card in the bottom-end corner of the viewport, under the launcher
 * that opened it (Putra's 2026-10-16 update). The body switches with the
 * slice's stage machine:
 *   suggestions → chatting (messages + input) → rating → thanks.
 *
 * Resizable like a window: drag the TOP edge (height), the START edge
 * (width — left in LTR, right in RTL), or the top-start CORNER (both).
 * The panel is anchored bottom-end, so dragging away from the anchor grows
 * it; the slice clamps to viewport-aware bounds.
 */
const HelpAgentPanel = () => {
  const { t } = useTranslation('help_agent')
  const helpStage = useAppStore((s) => s.helpStage)
  // `?? DEFAULT`: belt-and-braces for a hot-reloaded store singleton that
  // predates this state key — the panel must never render unsized.
  const helpPanelSize = useAppStore((s) => s.helpPanelSize) ?? HELP_PANEL_DEFAULT_SIZE
  const setHelpPanelSize = useAppStore((s) => s.setHelpPanelSize)
  const closeHelpAgent = useAppStore((s) => s.closeHelpAgent)
  const startNewHelpChat = useAppStore((s) => s.startNewHelpChat)

  const dragRef = useRef<{
    pointerId: number
    axis: ResizeAxis
    startX: number
    startY: number
    startWidth: number
    startHeight: number
  } | null>(null)

  const startResize = useCallback(
    (axis: ResizeAxis) => (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return
      const startSize = useAppStore.getState().helpPanelSize ?? HELP_PANEL_DEFAULT_SIZE
      dragRef.current = {
        pointerId: event.pointerId,
        axis,
        startX: event.clientX,
        startY: event.clientY,
        startWidth: startSize.width,
        startHeight: startSize.height,
      }
      // Optional-call: jsdom doesn't implement pointer capture.
      event.currentTarget.setPointerCapture?.(event.pointerId)
      event.preventDefault()
    },
    // Stable: the start size is read from the store at drag-start (below),
    // so this callback is NOT recreated on every pointermove-driven resize.
    []
  )

  const onResizePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return
      // Anchored bottom-end: in LTR the panel grows as the pointer moves LEFT
      // (away from the end edge); mirrored in RTL. Height grows UPWARD.
      const horizontal =
        getCurrentDirection() === 'rtl' ? event.clientX - drag.startX : drag.startX - event.clientX
      setHelpPanelSize?.({
        width: drag.axis === 'y' ? drag.startWidth : drag.startWidth + horizontal,
        height: drag.axis === 'x' ? drag.startHeight : drag.startHeight + (drag.startY - event.clientY),
      })
    },
    [setHelpPanelSize]
  )

  const onResizePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }, [])

  const sharedResizeHandlers = {
    onPointerMove: onResizePointerMove,
    onPointerUp: onResizePointerUp,
    onPointerCancel: onResizePointerUp,
  }

  return (
    <div
      role="dialog"
      aria-label={t('header.name')}
      style={{
        width: helpPanelSize.width,
        height: helpPanelSize.height,
        maxWidth: HELP_PANEL_MAX_WIDTH,
        maxHeight: HELP_PANEL_MAX_HEIGHT,
      }}
      className={`fixed ${HELP_PANEL_INSET} ${HELP_WIDGET_Z} flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-dark-border dark:bg-dark-card`}
    >
      {/* Resize surfaces — generous invisible hit areas on the two free
          edges (top and start, the ones not pinned to the corner) + a
          visible grip on the corner they share. The grip overlaps the start
          of the header strip, which holds nothing clickable there. */}
      <div
        data-testid="help-panel-resize-top"
        aria-hidden="true"
        onPointerDown={startResize('y')}
        {...sharedResizeHandlers}
        className="absolute top-0 z-20 h-2.5 cursor-ns-resize touch-none inset-x-6"
      />
      <div
        data-testid="help-panel-resize-side"
        aria-hidden="true"
        onPointerDown={startResize('x')}
        {...sharedResizeHandlers}
        className="absolute start-0 z-20 w-2.5 cursor-ew-resize touch-none inset-y-6"
      />
      <div
        data-testid="help-panel-resize-handle"
        aria-hidden="true"
        onPointerDown={startResize('both')}
        {...sharedResizeHandlers}
        className="group absolute start-0 top-0 z-20 flex size-6 cursor-nwse-resize touch-none items-start justify-start p-1 rtl:cursor-nesw-resize"
      >
        <svg
          viewBox="0 0 10 10"
          aria-hidden="true"
          className="size-3 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 rtl:-scale-x-100 dark:text-dark-border"
        >
          <path d="M1 9V1h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <HelpAgentHeader onNewChat={startNewHelpChat} onClose={closeHelpAgent} />
      {helpStage === 'suggestions' && (
        <>
          <div className="flex-1 overflow-y-auto">
            <SuggestedQuestions />
          </div>
          <HelpAgentInput />
        </>
      )}
      {helpStage === 'chatting' && (
        <>
          <HelpAgentMessages />
          <HelpAgentInput />
        </>
      )}
      {helpStage === 'rating' && <RatingView />}
      {helpStage === 'thanks' && <ThanksView />}
    </div>
  )
}

export default HelpAgentPanel
